// ============================================================================
// Code to Graph Sync Handler
// ============================================================================

import { generateConditionId } from '@polyai/stream/utils/prettyUuid';
import { toPythonicIdentifier } from '@polyai/stream/utils/stringHelper';

import type { Condition, Flow, FunctionStep } from '../../types';
import { ConditionType, IngressPosition } from '../../types';
import { blockIdentifier } from '../parser/block-identifier';
import type {
  CodeToGraphSyncResult,
  ConditionBlockBoundary,
  ExtractedCondition,
} from '../types';

/**
 * Syncs graph from code changes
 * Extracts conditions from code and updates the graph accordingly
 * @param {Flow} flow - The flow containing all steps
 * @param {FunctionStep} step - The function step to sync conditions for
 * @param {string} code - The Python code to extract conditions from
 * @return {CodeToGraphSyncResult} Result containing synced conditions and any errors
 */
export function syncGraphFromCode(
  flow: Flow,
  step: FunctionStep,
  code: string,
): CodeToGraphSyncResult {
  if (!code || code.trim().length === 0) {
    return {
      conditions: step.conditions,
    };
  }

  // Extract uncommented conditions from code
  let extractedConditions: ExtractedCondition[] = [];
  let allBlocks: ConditionBlockBoundary[] = [];

  try {
    // Find all condition blocks first (including commented ones)
    allBlocks = blockIdentifier.findConditionBlocks(code);

    // Extract only uncommented conditions
    extractedConditions = blockIdentifier.extractConditions(code);
  } catch (error) {
    console.error('Error extracting conditions from code:', error);
    // If parsing fails, preserve all existing conditions to avoid data loss
    return {
      conditions: step.conditions,
      errors: [
        {
          type: 'parse_error',
          message:
            error instanceof Error ? error.message : 'Failed to parse code',
        },
      ],
    };
  }

  // Safety check: if neither parser found anything and we had conditions before,
  // preserve them to avoid data loss (code might be invalid or unparseable).
  // But if extractConditions found conditions (even without `= False` blocks),
  // the code IS parseable — continue processing so stale conditions get removed.
  if (
    allBlocks.length === 0 &&
    extractedConditions.length === 0 &&
    step.conditions.length > 0 &&
    code.trim().length > 0
  ) {
    // Neither parser found anything but code exists and we had conditions
    // Preserve existing conditions to avoid data loss
    console.warn(
      'No condition blocks found in code, but conditions exist in graph. Preserving existing conditions.',
    );
    return {
      conditions: step.conditions,
      warnings: ['Could not parse condition blocks from code'],
    };
  }

  // If no uncommented conditions found, we still need to check for commented ones
  // and preserve conditions that aren't in code at all

  // Build step name to ID map from existing steps only
  // We don't create new steps - if a step doesn't exist, we skip that condition
  const stepNameToIdMap = buildStepNameToIdMap(flow);

  // Sync conditions (pass allBlocks and code to detect partially-commented blocks)
  const conditions = syncConditions(
    flow,
    step,
    extractedConditions,
    stepNameToIdMap,
    allBlocks,
    code,
  );

  return {
    conditions,
  };
}

/**
 * Builds a map of step names to step IDs
 * @param {Flow} flow - The flow containing all steps
 * @return {Record<string, string>} Map of step names to step IDs
 */
function buildStepNameToIdMap(flow: Flow): Record<string, string> {
  const nameToIdMap: Record<string, string> = {};
  Object.values(flow.steps.entities).forEach(step => {
    if (step) {
      nameToIdMap[step.name] = step.id;
    }
  });
  return nameToIdMap;
}

/**
 * Syncs conditions from extracted conditions
 * Removes conditions that are commented out in code (including partially-commented blocks)
 * Preserves conditions that don't exist in code at all
 * @param {Flow} flow - The flow containing all steps
 * @param {FunctionStep} step - The function step to sync conditions for
 * @param {ExtractedCondition[]} extractedConditions - Conditions extracted from code
 * @param {Record<string, string>} stepNameToIdMap - Map of step names to step IDs
 * @param {ConditionBlockBoundary[]} allBlocks - All condition blocks found in code (including commented)
 * @param {string} code - The raw Python code (for line-level comment checks)
 * @return {Condition[]} Array of synced conditions
 */
function syncConditions(
  flow: Flow,
  step: FunctionStep,
  extractedConditions: ExtractedCondition[],
  stepNameToIdMap: Record<string, string>,
  allBlocks: ConditionBlockBoundary[],
  code: string,
): Condition[] {
  // Build map of existing conditions by variable name (derived from label)
  // This allows us to match conditions even when labels change
  const existingConditionsByVariableName = new Map<string, Condition>();
  const existingConditionsByLabel = new Map<string, Condition>();
  // Also build a map by childStepId to match when labels change but step reference stays the same
  const existingConditionsByStepId = new Map<string, Condition[]>();

  step.conditions.forEach(condition => {
    if (condition.config.$case !== 'exitFlowCondition') {
      const label = condition.config.value.details.label;
      const variableName = toPythonicIdentifier(label);
      const childStepId = condition.config.value.childStepId;

      existingConditionsByVariableName.set(variableName, condition);
      existingConditionsByLabel.set(label, condition);

      // Group by childStepId for step-based matching
      if (!existingConditionsByStepId.has(childStepId)) {
        existingConditionsByStepId.set(childStepId, []);
      }
      existingConditionsByStepId.get(childStepId)?.push(condition);
    }
  });

  // Track which conditions are found in code (uncommented) by variable name
  const foundInCodeByVariable = new Set<string>();
  const foundInCodeByLabel = new Set<string>();
  // Also track which step IDs are found in code to prevent duplicates
  const foundInCodeByStepId = new Set<string>();
  // Track which condition IDs have already been added to prevent duplicates
  const addedConditionIds = new Set<string>();
  const newConditions: Condition[] = [];

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
  extractedConditions.forEach((extracted, index) => {
    // Handle exit flow conditions
    if (extracted.isExitFlow) {
      const variableName = toPythonicIdentifier(extracted.label);
      foundInCodeByVariable.add(variableName);
      foundInCodeByLabel.add(extracted.label);

      // Check if exit flow condition already exists by variable name
      const existingExitFlow = step.conditions.find(
        c =>
          c.config.$case === 'exitFlowCondition' &&
          toPythonicIdentifier(c.config.value.details.label) === variableName,
      );

      if (!existingExitFlow) {
        // Create new exit flow condition
        // Use the variable name with spaces as a readable label (e.g., "exit_flow" → "exit flow")
        const readableLabel = extracted.label.replace(/_/g, ' ');
        const conditionId = generateConditionId();
        const condition: Condition = {
          id: conditionId,
          config: {
            $case: ConditionType.ExitFlowCondition,
            value: {
              details: {
                label: readableLabel,
                position: {
                  x: step.position.x + 200 + index * 50,
                  y: step.position.y + 100 + index * 50,
                },
                requiredEntities: [],
                ingressPosition: IngressPosition.Top,
              },
              exitFlowPosition: {
                x: step.position.x + 200,
                y: step.position.y + 200,
              },
            },
          },
        };
        newConditions.push(condition);
      } else {
        // Preserve existing label — the extracted label is a variable name used
        // only for matching, not as the display label.
        newConditions.push(existingExitFlow);
      }
      return;
    }

    // Handle step-referencing conditions
    if (!extracted.stepName) {
      return; // Skip if no stepName (shouldn't happen for step conditions, but safety check)
    }

    const childStepId = stepNameToIdMap[extracted.stepName];

    if (!childStepId) {
      // Step not found - track that we saw this condition in code but skipped it
      // This prevents preserving old conditions with the same variable name
      const variableName = toPythonicIdentifier(extracted.label);
      foundInCodeByVariable.add(variableName);
      foundInCodeByLabel.add(extracted.label);
      return; // Step not found - responsibility is on the user
    }

    const targetStep = flow.steps.entities[childStepId];
    if (!targetStep) {
      // Step entity not found - track that we saw this condition in code but skipped it
      const variableName = toPythonicIdentifier(extracted.label);
      foundInCodeByVariable.add(variableName);
      foundInCodeByLabel.add(extracted.label);
      return; // Step entity not found
    }

    // Match by variable name first (more reliable when labels change)
    const variableName = toPythonicIdentifier(extracted.label);
    let existingCondition = existingConditionsByVariableName.get(variableName);

    // If not found by variable name, try matching by label (for backward compatibility)
    if (!existingCondition) {
      existingCondition = existingConditionsByLabel.get(extracted.label);
      // If found by label, also add it to variable name map for future lookups
      if (existingCondition) {
        existingConditionsByVariableName.set(variableName, existingCondition);
      }
    }

    // If still not found, try matching by step reference (childStepId)
    // This handles the case where label changes but the step reference stays the same
    // Also handles the case where a condition was just created via createStepCondition
    // and updateStep is running syncGraphFromCode before the state is fully updated
    if (!existingCondition) {
      const conditionsForStep = existingConditionsByStepId.get(childStepId);
      if (conditionsForStep) {
        // If there's exactly one condition for this step, use it
        // This is the common case since conditions can't share the same child step
        if (conditionsForStep.length === 1) {
          existingCondition = conditionsForStep[0];
          // Mark this variable name as found to prevent duplicate addition later
          // Also update the variable name map for future lookups
          existingConditionsByVariableName.set(variableName, existingCondition);
        } else if (conditionsForStep.length > 1) {
          // Multiple conditions for same step (shouldn't happen, but handle gracefully)
          // Try to match by label first
          const matchByLabel = conditionsForStep.find(
            c => c.config.value.details.label === extracted.label,
          );
          if (matchByLabel) {
            existingCondition = matchByLabel;
            // Mark this variable name as found to prevent duplicate addition later
            existingConditionsByVariableName.set(
              variableName,
              existingCondition,
            );
          } else {
            // If no label match, use the first one (shouldn't happen in practice)
            existingCondition = conditionsForStep[0];
            // Mark this variable name as found to prevent duplicate addition later
            existingConditionsByVariableName.set(
              variableName,
              existingCondition,
            );
          }
        }
      }
    }

    foundInCodeByVariable.add(variableName);
    foundInCodeByLabel.add(extracted.label);
    foundInCodeByStepId.add(childStepId);

    if (existingCondition) {
      // Update existing condition
      const existingConfig = existingCondition.config;
      if (existingConfig.$case !== 'exitFlowCondition') {
        existingConfig.value.childStepId = childStepId;
        // Update label if it changed
        if (existingConfig.value.details.label !== extracted.label) {
          existingConfig.value.details.label = extracted.label;
        }
      }
      // Only add if not already added (prevent duplicates)
      if (!addedConditionIds.has(existingCondition.id)) {
        newConditions.push(existingCondition);
        addedConditionIds.add(existingCondition.id);
      }
    } else {
      // Create new conditions for goto_step calls that don't match any existing
      // condition. This handles ADK push, flow duplication, and any other path
      // where conditions aren't pre-created via createStepCondition commands.
      // Deduplication is safe: when the UI dispatches createStepCondition before
      // updateStep, the condition already exists and is matched above by childStepId.
      const conditionId = generateConditionId();
      const condition: Condition = {
        id: conditionId,
        config: {
          $case: ConditionType.FunctionStepCondition,
          value: {
            childStepId,
            details: {
              label: extracted.label,
              position: {
                x: step.position.x + 200 + index * 50,
                y: step.position.y + 100 + index * 50,
              },
              requiredEntities: [],
              ingressPosition: IngressPosition.Top,
            },
          },
        },
      };
      newConditions.push(condition);
      addedConditionIds.add(conditionId);
    }
  });

  // Handle existing conditions that weren't found in extracted list
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
  existingConditionsByVariableName.forEach((condition, variableName) => {
    if (!foundInCodeByVariable.has(variableName)) {
      // Check if a block with this variable name exists in allBlocks.
      // If it does, the block is in code but non-functional (goto_step commented/removed)
      // → remove the condition from graph.
      // This handles both fully-commented blocks AND partially-commented blocks
      // (e.g., var/if lines uncommented but goto_step line commented out).
      const blockExistsInCode = allBlocks.some(
        b => b.variableName === variableName,
      );
      if (blockExistsInCode) {
        return;
      }

      // Check if a condition with the same step ID was found in code
      // This handles the case where a condition was just created via createStepCondition
      // and updateStep is running syncGraphFromCode - the condition should be preserved
      const conditionStepId =
        condition.config.$case !== 'exitFlowCondition'
          ? condition.config.value.childStepId
          : undefined;

      if (conditionStepId && foundInCodeByStepId.has(conditionStepId)) {
        // A condition with this step ID was found in code, so this condition should be preserved
        // (it's the same condition, just matched by step ID instead of variable name)
        // Only add if not already added (prevent duplicates)
        if (!addedConditionIds.has(condition.id)) {
          newConditions.push(condition);
          addedConditionIds.add(condition.id);
        }
        return;
      }

      // Condition doesn't exist in code at all.
      // If extractedConditions found active conditions, the code is parseable and
      // this condition was deliberately removed → don't preserve it.
      // If nothing was extracted (and no blocks found), we can't be sure
      // the code is parseable → preserve to avoid data loss.
      if (extractedConditions.length === 0) {
        if (!addedConditionIds.has(condition.id)) {
          newConditions.push(condition);
          addedConditionIds.add(condition.id);
        }
      }
    }
  });

  // Also handle exit flow conditions that might be commented
  const codeLines = code.split('\n');
  step.conditions.forEach(condition => {
    if (condition.config.$case === 'exitFlowCondition') {
      const label = condition.config.value.details.label;
      const variableName = toPythonicIdentifier(label);
      // If it's in the extracted list, it's already handled above
      if (foundInCodeByVariable.has(variableName)) {
        return;
      }
      // Check if a block with this variable name exists in allBlocks.
      // For exit flow: check if the exit_flow() call line is commented.
      // If the call line is commented (fully or partially commented block), remove it.
      // If the block doesn't exist in code at all, preserve it.
      const matchingBlock = allBlocks.find(
        b => b.variableName === variableName && b.isExitFlow,
      );
      if (matchingBlock) {
        // Block exists in code — check if the exit_flow call is commented
        const endLine = codeLines[matchingBlock.endLine];
        if (
          matchingBlock.isCommented ||
          (endLine && endLine.trim().startsWith('#'))
        ) {
          return; // exit_flow() call is commented → remove from graph
        }
        // exit_flow() call is not commented → still functional, preserve
        newConditions.push(condition);
        return;
      }
      // Block not in code at all → preserve it
      newConditions.push(condition);
    }
  });

  return newConditions;
}
